import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  CalendarBlank, 
  CurrencyDollar,
  Plus,
  ArrowClockwise,
  CheckCircle,
  Clock,
  Warning,
  User,
  CreditCard,
  CaretDown,
  X,
  Receipt
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PaymentPlansPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [patients, setPatients] = useState([]);
  const [patientInvoices, setPatientInvoices] = useState([]);
  
  // Create form state
  const [formData, setFormData] = useState({
    patient_id: '',
    invoice_ids: [],
    total_amount: 0,
    down_payment: 0,
    num_installments: 3,
    payment_day: 1,
    notes: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPlans();
    if (user?.role !== 'patient') {
      fetchPatients();
    }
  }, [user]);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/billing/payment-plans');
      setPlans(response.data.payment_plans || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching payment plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/admin/users?role=patient');
      const patientList = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setPatients(patientList);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching patients:', error);
    }
  };

  const fetchPatientInvoices = async (patientId) => {
    try {
      const response = await api.get(`/billing/invoices?patient_id=${patientId}`);
      // Filter to unpaid invoices without payment plans
      const unpaidInvoices = (response.data || []).filter(inv => 
        inv.balance_due > 0 && !inv.payment_plan_id
      );
      setPatientInvoices(unpaidInvoices);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching invoices:', error);
    }
  };

  const handlePatientChange = (patientId) => {
    setFormData(prev => ({ ...prev, patient_id: patientId, invoice_ids: [], total_amount: 0 }));
    if (patientId) {
      fetchPatientInvoices(patientId);
    } else {
      setPatientInvoices([]);
    }
  };

  const toggleInvoice = (invoice) => {
    const isSelected = formData.invoice_ids.includes(invoice.id);
    let newIds, newTotal;
    
    if (isSelected) {
      newIds = formData.invoice_ids.filter(id => id !== invoice.id);
      newTotal = formData.total_amount - invoice.balance_due;
    } else {
      newIds = [...formData.invoice_ids, invoice.id];
      newTotal = formData.total_amount + invoice.balance_due;
    }
    
    setFormData(prev => ({
      ...prev,
      invoice_ids: newIds,
      total_amount: Math.round(newTotal * 100) / 100
    }));
  };

  const createPlan = async () => {
    if (!formData.patient_id || formData.invoice_ids.length === 0) {
      toast.error('Please select a patient and at least one invoice');
      return;
    }
    
    if (formData.down_payment >= formData.total_amount) {
      toast.error('Down payment must be less than total amount');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/billing/payment-plans', formData);
      toast.success('Payment plan created successfully');
      setShowCreateModal(false);
      setFormData({
        patient_id: '',
        invoice_ids: [],
        total_amount: 0,
        down_payment: 0,
        num_installments: 3,
        payment_day: 1,
        notes: ''
      });
      setPatientInvoices([]);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment plan');
    } finally {
      setCreating(false);
    }
  };

  const payInstallment = async (planId, installmentNumber, amount) => {
    try {
      const response = await api.post(`/billing/payment-plans/${planId}/pay/${installmentNumber}`, {
        amount,
        payment_method: 'card'
      });
      toast.success(response.data.message);
      fetchPlans();
      if (selectedPlan?.id === planId) {
        setSelectedPlan(response.data.plan);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      defaulted: 'bg-red-100 text-red-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getInstallmentStatus = (inst) => {
    const dueDate = new Date(inst.due_date);
    const now = new Date();
    
    if (inst.status === 'paid') return { color: 'text-green-600', bg: 'bg-green-50', label: 'Paid' };
    if (inst.status === 'partial') return { color: 'text-amber-600', bg: 'bg-amber-50', label: 'Partial' };
    if (dueDate < now) return { color: 'text-red-600', bg: 'bg-red-50', label: 'Overdue' };
    return { color: 'text-slate-600', bg: 'bg-slate-50', label: 'Pending' };
  };

  // Calculate monthly payment preview
  const monthlyPayment = formData.total_amount > 0 && formData.num_installments > 0
    ? (formData.total_amount - formData.down_payment) / formData.num_installments
    : 0;

  return (
    <div className="p-8" data-testid="payment-plans-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Payment Plans
          </h1>
          <p className="text-slate-500">Manage patient installment payment plans</p>
        </div>
        
        {user?.role !== 'patient' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-teal flex items-center gap-2"
          >
            <Plus size={18} />
            Create Payment Plan
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Active Plans</p>
          <p className="text-2xl font-bold text-blue-600">
            {plans.filter(p => p.status === 'active').length}
          </p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Total Managed</p>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(plans.reduce((sum, p) => sum + (p.total_amount || 0), 0))}
          </p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Collected</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(plans.reduce((sum, p) => sum + (p.total_paid || 0), 0))}
          </p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Completed Plans</p>
          <p className="text-2xl font-bold text-teal-600">
            {plans.filter(p => p.status === 'completed').length}
          </p>
        </div>
      </div>

      {/* Plans List */}
      <div className="card-clinical overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Total</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Paid</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Remaining</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Installments</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Next Due</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-slate-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Loading payment plans...
                </td>
              </tr>
            ) : plans.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-slate-500">
                  <CalendarBlank size={48} className="mx-auto mb-2 text-slate-300" />
                  No payment plans found
                </td>
              </tr>
            ) : (
              plans.map((plan) => (
                <tr
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan)}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{plan.patient_name}</p>
                    <p className="text-xs text-slate-500">{plan.invoice_ids?.length || 0} invoice(s)</p>
                  </td>
                  <td className="py-3 px-4 text-right font-medium">
                    {formatCurrency(plan.total_amount)}
                  </td>
                  <td className="py-3 px-4 text-right text-green-600">
                    {formatCurrency(plan.total_paid)}
                  </td>
                  <td className="py-3 px-4 text-right text-amber-600">
                    {formatCurrency(plan.remaining_balance - (plan.total_paid - plan.down_payment))}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-sm">
                      {plan.installments?.filter(i => i.status === 'paid').length || 0}/{plan.num_installments}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {plan.next_due_date ? formatDate(plan.next_due_date) : '-'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {getStatusBadge(plan.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Payment Plan Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Create Payment Plan</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            {/* Patient Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Select Patient</label>
              <select
                value={formData.patient_id}
                onChange={(e) => handlePatientChange(e.target.value)}
                className="input-clinical"
              >
                <option value="">Choose a patient...</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                ))}
              </select>
            </div>
            
            {/* Invoice Selection */}
            {formData.patient_id && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Invoices ({patientInvoices.length} available)
                </label>
                <div className="max-h-40 overflow-y-auto space-y-2 border rounded-lg p-2">
                  {patientInvoices.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">No unpaid invoices</p>
                  ) : (
                    patientInvoices.map((inv) => (
                      <label
                        key={inv.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                          formData.invoice_ids.includes(inv.id)
                            ? 'bg-teal-50 border border-teal-200'
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={formData.invoice_ids.includes(inv.id)}
                            onChange={() => toggleInvoice(inv)}
                            className="w-4 h-4 text-teal-600"
                          />
                          <div>
                            <p className="text-sm font-medium">{formatDate(inv.created_at)}</p>
                            <p className="text-xs text-slate-500">{inv.items?.length || 0} item(s)</p>
                          </div>
                        </div>
                        <span className="font-medium text-teal-600">{formatCurrency(inv.balance_due)}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
            
            {/* Plan Details */}
            {formData.total_amount > 0 && (
              <>
                <div className="bg-teal-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-teal-700">Selected Total</p>
                  <p className="text-2xl font-bold text-teal-800">{formatCurrency(formData.total_amount)}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Down Payment</label>
                    <input
                      type="number"
                      min="0"
                      max={formData.total_amount - 1}
                      value={formData.down_payment}
                      onChange={(e) => setFormData(prev => ({ ...prev, down_payment: parseFloat(e.target.value) || 0 }))}
                      className="input-clinical"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Number of Installments</label>
                    <select
                      value={formData.num_installments}
                      onChange={(e) => setFormData(prev => ({ ...prev, num_installments: parseInt(e.target.value) }))}
                      className="input-clinical"
                    >
                      {[2, 3, 4, 6, 9, 12].map((n) => (
                        <option key={n} value={n}>{n} payments</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Payment Day of Month</label>
                    <select
                      value={formData.payment_day}
                      onChange={(e) => setFormData(prev => ({ ...prev, payment_day: parseInt(e.target.value) }))}
                      className="input-clinical"
                    >
                      {[1, 5, 10, 15, 20, 25].map((d) => (
                        <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <div className="bg-slate-100 rounded-lg p-3 w-full">
                      <p className="text-xs text-slate-500">Monthly Payment</p>
                      <p className="text-xl font-bold text-slate-900">{formatCurrency(monthlyPayment)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="input-clinical"
                    rows={2}
                    placeholder="Any special arrangements or notes..."
                  />
                </div>
              </>
            )}
            
            <div className="flex gap-3">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 btn-outline">
                Cancel
              </button>
              <button
                onClick={createPlan}
                disabled={formData.invoice_ids.length === 0 || creating}
                className="flex-1 btn-teal"
              >
                {creating ? <ArrowClockwise size={18} className="animate-spin" /> : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Detail Modal */}
      {selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Payment Plan Details</h2>
                <p className="text-slate-500">{selectedPlan.patient_name}</p>
              </div>
              {getStatusBadge(selectedPlan.status)}
            </div>
            
            {/* Plan Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-500">Total Amount</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(selectedPlan.total_amount)}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600">Total Paid</p>
                <p className="text-xl font-bold text-green-700">{formatCurrency(selectedPlan.total_paid)}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4">
                <p className="text-sm text-amber-600">Remaining</p>
                <p className="text-xl font-bold text-amber-700">
                  {formatCurrency(selectedPlan.total_amount - selectedPlan.total_paid)}
                </p>
              </div>
            </div>
            
            {/* Installments */}
            <h3 className="font-medium text-slate-900 mb-3">Installment Schedule</h3>
            <div className="space-y-2 mb-6">
              {selectedPlan.down_payment > 0 && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-green-500" />
                    <div>
                      <p className="font-medium text-slate-900">Down Payment</p>
                      <p className="text-xs text-slate-500">At sign-up</p>
                    </div>
                  </div>
                  <span className="font-bold text-green-600">{formatCurrency(selectedPlan.down_payment)}</span>
                </div>
              )}
              
              {selectedPlan.installments?.map((inst) => {
                const status = getInstallmentStatus(inst);
                return (
                  <div key={inst.installment_number} className={`flex items-center justify-between p-3 ${status.bg} rounded-lg`}>
                    <div className="flex items-center gap-3">
                      {inst.status === 'paid' ? (
                        <CheckCircle size={20} className="text-green-500" />
                      ) : (
                        <Clock size={20} className={status.color} />
                      )}
                      <div>
                        <p className="font-medium text-slate-900">Installment {inst.installment_number}</p>
                        <p className="text-xs text-slate-500">Due: {formatDate(inst.due_date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-bold ${status.color}`}>
                        {formatCurrency(inst.amount)}
                      </span>
                      {inst.status !== 'paid' && user?.role !== 'patient' && (
                        <button
                          onClick={() => payInstallment(selectedPlan.id, inst.installment_number, inst.amount)}
                          className="text-xs bg-teal-600 text-white px-3 py-1 rounded-full hover:bg-teal-700"
                        >
                          Record Payment
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {selectedPlan.notes && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-500 mb-1">Notes</p>
                <p className="text-slate-700">{selectedPlan.notes}</p>
              </div>
            )}
            
            <button onClick={() => setSelectedPlan(null)} className="w-full btn-outline">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
